import { ReactNode } from "react";
import { TableBody, TableHead, TableRow } from "@mui/material";
import { StyledTable, StyledTableCell } from "../../../shared/components/CustomTable/Styled";
import { StyledNavLink } from "../../../shared/styled/TableStyles";
import { Cell, DailyPlan, PlanTrip, matrixRows, tripCells } from "./plan";
import { formatInternationalNumber } from "../../../utils/format";
import { PlanTableContainer } from "./Styled";

const tripHref = (cell: Cell) =>
    `/product-orders/${cell.trip.sale_order_id}?tab=delivery%20trips&deliveryTrip=${cell.trip.id}`;

function PlanCell({
    printable,
    component = "td",
    ...props
}: {
    printable: boolean;
    component?: "td" | "th";
    children?: ReactNode;
    className?: string;
    colSpan?: number;
    rowSpan?: number;
    scope?: "col" | "colgroup" | "row";
}) {
    if (printable) {
        const Tag = component;
        return <Tag {...props} />;
    }
    return <StyledTableCell component={component} {...props} />;
}

function CellColumns({ cell, printable }: { cell?: Cell; printable: boolean }) {
    return (
        <>
            <PlanCell printable={printable} className="plan-quantity">
                {cell?.quantity === "" || !cell ? "" : formatInternationalNumber(cell.quantity)}
            </PlanCell>
            <PlanCell printable={printable}>
                {cell && (
                    <div className="plan-item">
                        <div className="plan-item-name">
                            {cell.name}
                            {!printable && cell.unit && <span className="plan-unit"> · {cell.unit}</span>}
                        </div>
                        {printable && cell.unit && <div className="plan-unit">ĐVT: {cell.unit}</div>}
                        <div className="plan-order">
                            {printable ? (
                                cell.trip.order_name
                            ) : (
                                <StyledNavLink to={tripHref(cell)}>
                                    {cell.trip.order_name || "Chi tiết chuyến"}
                                </StyledNavLink>
                            )}
                        </div>
                        {cell.trip.customer && <div className="plan-customer">{cell.trip.customer}</div>}
                        {cell.trip.delivery_address && <div className="plan-address">{cell.trip.delivery_address}</div>}
                        {cell.trip.notes && <div className="plan-note">Ghi chú: {cell.trip.notes}</div>}
                    </div>
                )}
            </PlanCell>
        </>
    );
}

// On screen, keep one vehicle row and one set of delivery details per order/trip.
// Print and Excel retain their item-by-item layout for pagination and export.
function TripGroup({ trip }: { trip: PlanTrip }) {
    const items = tripCells([trip]);
    return (
        <div className="plan-trip-group">
            {items.map((item, index) => (
                <div className="plan-item" key={trip.items[index]?.id || trip.id}>
                    <span className="plan-quantity">
                        {item.quantity === "" ? "" : formatInternationalNumber(item.quantity)}
                    </span>
                    <span className="plan-item-name">
                        {item.name}{item.unit && <span className="plan-unit"> · {item.unit}</span>}
                    </span>
                </div>
            ))}
            <div className="plan-trip-details">
                <StyledNavLink to={tripHref(items[0])}>{trip.order_name || "Chi tiết chuyến"}</StyledNavLink>
                {trip.customer && <div className="plan-customer">{trip.customer}</div>}
                {trip.delivery_address && <div className="plan-address">{trip.delivery_address}</div>}
                {trip.notes && <div className="plan-note">Ghi chú: {trip.notes}</div>}
            </div>
        </div>
    );
}

export default function PlanDocument({ data, printable = false }: { data: DailyPlan; printable?: boolean }) {
    // The application uses the same table primitives as ListView. The isolated print
    // document uses plain HTML so application CSS cannot leak into printed pages.
    const Table = printable ? "table" : StyledTable;
    const Head = printable ? "thead" : TableHead;
    const Body = printable ? "tbody" : TableBody;
    const Row = printable ? "tr" : TableRow;
    const Scroll = printable ? "div" : PlanTableContainer;
    return (
        <section id="daily-delivery-plan">
            {printable && (
                <div className="plan-heading">
                    <h2>Lịch giao hàng</h2>
                    <span>{data.date.split("-").reverse().join("/")}</span>
                </div>
            )}
            {!data.vehicles.length && !data.unassigned.length && (
                <p className="plan-empty">Không có lịch giao hàng trong ngày.</p>
            )}
            <Scroll className="plan-scroll" tabIndex={0} role="region" aria-label="Kế hoạch theo xe và chuyến">
                <Table className="delivery-plan-table plan-matrix" {...(printable ? {} : { stickyHeader: true })}>
                    <colgroup>
                        <col className="plan-vehicle-column" />
                        {[1, 2, 3, 4, 5].map((n) => (
                            <ColumnPair key={n} />
                        ))}
                    </colgroup>
                    <Head>
                        <Row>
                            <PlanCell printable={printable} component="th" rowSpan={2} scope="col" className="plan-vehicle">
                                Số xe
                            </PlanCell>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <PlanCell printable={printable} component="th" key={n} colSpan={2} scope="colgroup">
                                    Chuyến {n}
                                </PlanCell>
                            ))}
                        </Row>
                        <Row>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <HeaderPair key={n} printable={printable} />
                            ))}
                        </Row>
                    </Head>
                    <Body>
                        {printable ? matrixRows(data).map((row, index) => (
                            <Row key={index}>
                                <PlanCell printable={printable} component="th" scope="row" className="plan-vehicle">
                                    {row.vehicle}
                                </PlanCell>
                                {row.cells.map((cell, n) => (
                                    <CellColumns key={n} cell={cell} printable={printable} />
                                ))}
                            </Row>
                        )) : data.vehicles.map((vehicle) => (
                            <Row key={vehicle.vehicle_plate}>
                                <PlanCell printable={false} component="th" scope="row" className="plan-vehicle">
                                    {vehicle.vehicle_plate}
                                </PlanCell>
                                {[1, 2, 3, 4, 5].map((slot) => (
                                    <PlanCell printable={false} colSpan={2} className="plan-trip-cell" key={slot}>
                                        {(vehicle.trips[String(slot)] || []).map((trip) => <TripGroup key={trip.id} trip={trip} />)}
                                    </PlanCell>
                                ))}
                            </Row>
                        ))}
                    </Body>
                </Table>
            </Scroll>
            {data.unassigned.length > 0 && (
                <>
                    <h3>Chưa phân chuyến</h3>
                    <Scroll className="plan-scroll">
                        <Table
                            className="delivery-plan-table plan-unassigned"
                            {...(printable ? {} : { stickyHeader: true })}
                        >
                            {!printable && (
                                <colgroup>
                                    <col className="plan-vehicle-column" />
                                    <col className="plan-quantity-column" />
                                    <col />
                                </colgroup>
                            )}
                            <Head>
                                <Row>
                                    <PlanCell printable={printable} component="th" className="plan-vehicle">
                                        Số xe
                                    </PlanCell>
                                    <HeaderPair printable={printable} />
                                </Row>
                            </Head>
                            <Body>
                                {printable ? tripCells(data.unassigned).map((cell, index) => (
                                    <Row key={index}>
                                        <PlanCell printable={printable} component="th" scope="row">
                                            {cell.trip.vehicle_plate || "Chưa có xe"}
                                        </PlanCell>
                                        <CellColumns cell={cell} printable={printable} />
                                    </Row>
                                )) : data.unassigned.map((trip) => (
                                    <Row key={trip.id}>
                                        <PlanCell printable={false} component="th" scope="row" className="plan-vehicle">
                                            {trip.vehicle_plate || "Chưa có xe"}
                                        </PlanCell>
                                        <PlanCell printable={false} colSpan={2} className="plan-trip-cell">
                                            <TripGroup trip={trip} />
                                        </PlanCell>
                                    </Row>
                                ))}
                            </Body>
                        </Table>
                    </Scroll>
                </>
            )}
            <div className="plan-totals">
                {Object.entries(data.totals).map(([group, quantity]) => (
                    <span key={group}>
                        Tổng {group}: <strong>{formatInternationalNumber(quantity)}</strong>
                    </span>
                ))}
            </div>
            {data.other_totals.length > 0 && (
                <>
                    <h3>Vật tư / sản phẩm khác</h3>
                    <div className="plan-other-totals">
                        {data.other_totals.map((item, index) => (
                            <div key={index}>
                                {item.name}:{" "}
                                <strong>
                                    {formatInternationalNumber(item.quantity)} {item.unit}
                                </strong>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
function HeaderPair({ printable }: { printable: boolean }) {
    return (
        <>
            <PlanCell printable={printable} component="th" scope="col" className="plan-quantity">
                Số lượng
            </PlanCell>
            <PlanCell printable={printable} component="th" scope="col">
                Tên hàng
            </PlanCell>
        </>
    );
}
const ColumnPair = () => (
    <>
        <col className="plan-quantity-column" />
        <col className="plan-item-column" />
    </>
);
